import {
  Component,
  Inject,
  OnInit,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { EmailService } from "src/app/services/email-service";

/** Column spec */
export interface ColumnSpec {
  header: string;
  field: string;
}

/** Preview returned by SQL test */
export interface DataPreview {
  cols: ColumnSpec[];
  products: any[];
}

/** Save result shape */
export interface DynamicEmailTableResult {
  placeholderName?: string;
  displayTitle?: string;
  sqlQuery?: string;
  secretKey?: string;

  // preview results
  cols?: ColumnSpec[];
  products?: any[];

  // header styling (for <th>)
  headerStyle?: {
    fontFamily?: string;
    fontSize?: string; // e.g. "16px"
    bgColor?: string;
    textColor?: string;
    fontWeight?: "normal" | "bold";
    fontStyle?: "normal" | "italic";
    textAlign?: "left" | "center" | "right";
    textDecoration?: string; // "underline line-through" etc
  };

  // body styling (for <td>)
  bodyStyle?: {
    fontFamily?: string;
    fontSize?: string;
    bgColor?: string;
    textColor?: string;
    fontWeight?: "normal" | "bold";
    fontStyle?: "normal" | "italic";
    textAlign?: "left" | "center" | "right";
    textDecoration?: string;
  };
}

type Mode = "new" | "preview" | "edit";
type HeadingLevel = "p" | "h1" | "h2" | "h3";
type StyleTarget = "header" | "body";

@Component({
    selector: "app-dynamic-email-table",
    templateUrl: "./dynamic-email-table.component.html",
    styleUrls: ["./dynamic-email-table.component.scss"],
    standalone: false
})
export class DynamicEmailTableComponent implements OnInit {
  @ViewChild("dialogContent", { static: false }) dialogContent!: ElementRef;

  form: FormGroup;

  // SQL test state
  testing = false;
  sqlTested = false;
  preview: DataPreview | null = null;

  // fonts shared for header + body
  headerFonts = [
    "Arial, sans-serif",
    "Helvetica, sans-serif",
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    "'Times New Roman', Times, serif",
    "Georgia, serif",
    "Courier New, monospace",
  ];

  // ----------------- HEADER STYLE STATE -----------------
  headerFontFamily = this.headerFonts[0];
  headerFontSizeNumber = 16;
  headerBgColor = "#f7f9fb";
  headerTextColor = "#000000";
  headerFontWeight: "normal" | "bold" = "bold";
  headerFontStyle: "normal" | "italic" = "normal";
  headerTextAlign: "left" | "center" | "right" = "left";
  headerUnderline = false;
  headerStrike = false;
  headerHeading: HeadingLevel = "p";

  // ----------------- BODY STYLE STATE -------------------
  bodyFontFamily = this.headerFonts[0];
  bodyFontSizeNumber = 14;
  bodyBgColor = "#ffffff";
  bodyTextColor = "#000000";
  bodyFontWeight: "normal" | "bold" = "normal";
  bodyFontStyle: "normal" | "italic" = "normal";
  bodyTextAlign: "left" | "center" | "right" = "left";
  bodyUnderline = false;
  bodyStrike = false;
  bodyHeading: HeadingLevel = "p";

  // which target the toolbar edits
  activeStyleTarget: StyleTarget = "header";

  // mode controls
  mode: Mode = "new";

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<
      DynamicEmailTableComponent,
      DynamicEmailTableResult | undefined
    >,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private emailService: EmailService
  ) {
    this.form = this.fb.group({
      placeholderName: [""],
      displayTitle: ["", Validators.required],
      sqlQuery: ["", Validators.required],
      secretKey: [""],
    });
  }

  ngOnInit(): void {
    const d = this.data || {};

    // console.log("Dynamic Email Table Dialog Data:", d);

    if (d.mode === "preview") this.mode = "preview";
    else if (d.mode === "edit") this.mode = "edit";
    else this.mode = "new";

    // If preview data passed in, use it
    if (d.cols && d.products) {
      this.preview = { cols: d.cols, products: d.products };
      this.sqlTested = true;
      // console.log("Preview data loaded:", this.preview);
    }

    // patch form fields when provided
    if (
      d.placeholderName !== undefined ||
      d.displayTitle !== undefined ||
      d.sqlQuery !== undefined ||
      d.secretKey !== undefined
    ) {
      const formValues = {
        placeholderName: d.placeholderName || "",
        displayTitle: d.displayTitle || "",
        sqlQuery: d.sqlQuery || "",
        secretKey: d.secretKey || "",
      };
      // console.log("Patching form with values:", formValues);
      this.form.patchValue(formValues);
    }

    // preload headerStyle if provided
    if (d.headerStyle) {
      const hs = d.headerStyle;
      this.headerFontFamily = hs.fontFamily || this.headerFontFamily;
      if (hs.fontSize) {
        const n = parseInt(String(hs.fontSize).replace("px", ""), 10);
        if (!isNaN(n)) this.headerFontSizeNumber = n;
      }
      this.headerBgColor = hs.bgColor || this.headerBgColor;
      this.headerTextColor = hs.textColor || this.headerTextColor;
      this.headerFontWeight = hs.fontWeight || this.headerFontWeight;
      this.headerFontStyle = hs.fontStyle || this.headerFontStyle;
      this.headerTextAlign = hs.textAlign || this.headerTextAlign;
      if (hs.textDecoration) {
        this.headerUnderline = hs.textDecoration.includes("underline");
        this.headerStrike = hs.textDecoration.includes("line-through");
      }
      // Try to infer heading from font size
      this.headerHeading = this.mapFontSizeToHeading(
        this.headerFontSizeNumber,
        "header"
      );
    }

    // preload bodyStyle if provided
    if (d.bodyStyle) {
      const bs = d.bodyStyle;
      this.bodyFontFamily = bs.fontFamily || this.bodyFontFamily;
      if (bs.fontSize) {
        const n = parseInt(String(bs.fontSize).replace("px", ""), 10);
        if (!isNaN(n)) this.bodyFontSizeNumber = n;
      }
      this.bodyBgColor = bs.bgColor || this.bodyBgColor;
      this.bodyTextColor = bs.textColor || this.bodyTextColor;
      this.bodyFontWeight = bs.fontWeight || this.bodyFontWeight;
      this.bodyFontStyle = bs.fontStyle || this.bodyFontStyle;
      this.bodyTextAlign = bs.textAlign || this.bodyTextAlign;
      if (bs.textDecoration) {
        this.bodyUnderline = bs.textDecoration.includes("underline");
        this.bodyStrike = bs.textDecoration.includes("line-through");
      }
      this.bodyHeading = this.mapFontSizeToHeading(
        this.bodyFontSizeNumber,
        "body"
      );
    }

    // Auto-trigger SQL test when in edit mode with existing SQL configuration
    if (
      this.mode === "edit" &&
      this.form.value.sqlQuery &&
      this.form.value.secretKey &&
      !this.preview
    ) {
      // console.log("Auto-triggering SQL test in edit mode");
      // Use setTimeout to ensure form is fully initialized
      setTimeout(() => {
        this.testSql();
      }, 100);
    }
  }

  // SQL testing
  testSql(): void {
    if (this.form.controls["sqlQuery"].invalid) {
      this.form.controls["sqlQuery"].markAsTouched();
      return;
    }

    this.testing = true;
    this.sqlTested = false;
    this.preview = null;

    const payload = {
      sqlQuery: this.form.value.sqlQuery,
      secretKey: this.form.value.secretKey || "",
    };

    // Call real API
    this.emailService
      .testSqlQuery(payload)
      .then((resp: any) => {
        // console.log("Test SQL Response:", resp);

        // Handle different response structures
        let cols = [];
        let products = [];

        // Check if response is directly an array (most common case)
        if (Array.isArray(resp)) {
          products = resp;

          // Generate columns from the first object's keys
          if (products.length > 0) {
            const firstRow = products[0];
            cols = Object.keys(firstRow).map((key) => ({
              header: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
              field: key,
            }));
          }
        }
        // Check if response has cols and products properties
        else if (resp && typeof resp === "object") {
          if (resp.cols && Array.isArray(resp.cols)) {
            cols = resp.cols;
          }

          if (resp.products && Array.isArray(resp.products)) {
            products = resp.products;
          } else if (resp.data && Array.isArray(resp.data)) {
            products = resp.data;
          }

          // If cols is empty but products has data, infer columns
          if (cols.length === 0 && products.length > 0) {
            const firstRow = products[0];
            cols = Object.keys(firstRow).map((key) => ({
              header: key.charAt(0).toUpperCase() + key.slice(1),
              field: key,
            }));
          }
        }

        this.preview = { cols, products };
        this.sqlTested = true;
        this.testing = false;

        // console.log("Preview set:", this.preview);
        // console.log("Columns:", cols);
        // console.log("Data rows:", products.length);

        // Apply header styles from response if available
        if (resp.headerStyle) {
          this.applyHeaderStyleFromResponse(resp.headerStyle);
        }

        // Apply body styles from response if available
        if (resp.bodyStyle) {
          this.applyBodyStyleFromResponse(resp.bodyStyle);
        }

        this.scrollToBottom();
      })
      .catch((error) => {
        console.error("Error testing SQL:", error);
        console.error("Error details:", {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error,
        });

        this.testing = false;
        this.sqlTested = false;

        // The raw response is logged above for support but not shown: on this
        // endpoint it is database output rather than anything a user can act on.
        let errorMessage = "Failed to test SQL query.";
        if (error.status === 400) {
          errorMessage =
            "Bad Request (400): Please check your SQL query and secret key format.";
        } else if (error.status === 401) {
          errorMessage = "Unauthorized (401): Authentication required.";
        } else if (error.status === 500) {
          errorMessage = "Server Error (500): Please contact support.";
        }

        alert(errorMessage);
      });
  }

  // Save
  save(): void {
    if (this.form.invalid || !this.sqlTested || !this.preview) {
      this.form.markAllAsTouched();
      return;
    }

    const headerTextDecoration = this.buildTextDecoration(
      this.headerUnderline,
      this.headerStrike
    );
    const bodyTextDecoration = this.buildTextDecoration(
      this.bodyUnderline,
      this.bodyStrike
    );

    const headerStyle = {
      fontFamily: this.headerFontFamily,
      fontSize: `${this.headerFontSizeNumber}px`,
      bgColor: this.headerBgColor,
      textColor: this.headerTextColor,
      fontWeight: this.headerFontWeight,
      fontStyle: this.headerFontStyle,
      textAlign: this.headerTextAlign,
      textDecoration: headerTextDecoration,
    };

    const bodyStyle = {
      fontFamily: this.bodyFontFamily,
      fontSize: `${this.bodyFontSizeNumber}px`,
      bgColor: this.bodyBgColor,
      textColor: this.bodyTextColor,
      fontWeight: this.bodyFontWeight,
      fontStyle: this.bodyFontStyle,
      textAlign: this.bodyTextAlign,
      textDecoration: bodyTextDecoration,
    };

    const payload: DynamicEmailTableResult = {
      placeholderName: this.form.value.placeholderName,
      displayTitle: this.form.value.displayTitle,
      sqlQuery: this.form.value.sqlQuery,
      secretKey: this.form.value.secretKey,
      cols: this.preview.cols,
      products: this.preview.products,
      headerStyle,
      bodyStyle,
    };

    this.dialogRef.close(payload);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // Helpers / computed
  get headerFontSizeString(): string {
    return `${this.headerFontSizeNumber}px`;
  }

  get bodyFontSizeString(): string {
    return `${this.bodyFontSizeNumber}px`;
  }

  get headerTextDecoration(): string {
    return this.buildTextDecoration(this.headerUnderline, this.headerStrike);
  }

  get bodyTextDecoration(): string {
    return this.buildTextDecoration(this.bodyUnderline, this.bodyStrike);
  }

  private buildTextDecoration(underline: boolean, strike: boolean): string {
    const parts: string[] = [];
    if (underline) parts.push("underline");
    if (strike) parts.push("line-through");
    return parts.length ? parts.join(" ") : "none";
  }

  // Toolbar "target" switch
  setActiveTarget(target: StyleTarget) {
    this.activeStyleTarget = target;
  }

  // Active heading binding for toolbar (getter/setter)
  get activeHeading(): HeadingLevel {
    return this.activeStyleTarget === "header"
      ? this.headerHeading
      : this.bodyHeading;
  }

  set activeHeading(value: HeadingLevel) {
    if (this.activeStyleTarget === "header") {
      this.headerHeading = value;
    } else {
      this.bodyHeading = value;
    }
    this.applyHeadingFontSize(value);
  }

  // Map heading choice to font size
  private applyHeadingFontSize(value: HeadingLevel) {
    let size: number;
    if (value === "h1") size = 26;
    else if (value === "h2") size = 22;
    else if (value === "h3") size = 18;
    else size = this.activeStyleTarget === "header" ? 16 : 14;

    if (this.activeStyleTarget === "header") {
      this.headerFontSizeNumber = size;
    } else {
      this.bodyFontSizeNumber = size;
    }
  }

  private mapFontSizeToHeading(
    size: number,
    target: StyleTarget
  ): HeadingLevel {
    if (size >= 24) return "h1";
    if (size >= 20) return "h2";
    if (size >= 17) return "h3";
    return target === "header" ? "p" : "p";
  }

  // Bold/Italic/Underline/Strike/Align handlers using active target
  isBoldActive(): boolean {
    return this.activeStyleTarget === "header"
      ? this.headerFontWeight === "bold"
      : this.bodyFontWeight === "bold";
  }

  toggleBold(): void {
    if (this.activeStyleTarget === "header") {
      this.headerFontWeight =
        this.headerFontWeight === "bold" ? "normal" : "bold";
    } else {
      this.bodyFontWeight = this.bodyFontWeight === "bold" ? "normal" : "bold";
    }
  }

  isItalicActive(): boolean {
    return this.activeStyleTarget === "header"
      ? this.headerFontStyle === "italic"
      : this.bodyFontStyle === "italic";
  }

  toggleItalic(): void {
    if (this.activeStyleTarget === "header") {
      this.headerFontStyle =
        this.headerFontStyle === "italic" ? "normal" : "italic";
    } else {
      this.bodyFontStyle =
        this.bodyFontStyle === "italic" ? "normal" : "italic";
    }
  }

  isUnderlineActive(): boolean {
    return this.activeStyleTarget === "header"
      ? this.headerUnderline
      : this.bodyUnderline;
  }

  toggleUnderline(): void {
    if (this.activeStyleTarget === "header") {
      this.headerUnderline = !this.headerUnderline;
    } else {
      this.bodyUnderline = !this.bodyUnderline;
    }
  }

  isStrikeActive(): boolean {
    return this.activeStyleTarget === "header"
      ? this.headerStrike
      : this.bodyStrike;
  }

  toggleStrike(): void {
    if (this.activeStyleTarget === "header") {
      this.headerStrike = !this.headerStrike;
    } else {
      this.bodyStrike = !this.bodyStrike;
    }
  }

  isAlignActive(dir: "left" | "center" | "right"): boolean {
    const current =
      this.activeStyleTarget === "header"
        ? this.headerTextAlign
        : this.bodyTextAlign;
    return current === dir;
  }

  setAlign(alignment: "left" | "center" | "right") {
    if (this.activeStyleTarget === "header") {
      this.headerTextAlign = alignment;
    } else {
      this.bodyTextAlign = alignment;
    }
  }

  get activeTextColor(): string {
    return this.activeStyleTarget === "header"
      ? this.headerTextColor
      : this.bodyTextColor;
  }

  get activeBgColor(): string {
    return this.activeStyleTarget === "header"
      ? this.headerBgColor
      : this.bodyBgColor;
  }

  onTextColorChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (this.activeStyleTarget === "header") {
      this.headerTextColor = value;
    } else {
      this.bodyTextColor = value;
    }
  }

  onBgColorChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (this.activeStyleTarget === "header") {
      this.headerBgColor = value;
    } else {
      this.bodyBgColor = value;
    }
  }

  // Helper method to apply header styles from API response
  private applyHeaderStyleFromResponse(headerStyle: any): void {
    const DEFAULT_HEADER_BG = "#f7f9fb";
    const DEFAULT_HEADER_TEXT = "#000000";
    const DEFAULT_HEADER_FONT_FAMILY =
      this.headerFonts[0] || "Arial, sans-serif";
    const DEFAULT_HEADER_FONT_SIZE = 16;

    this.headerBgColor = headerStyle?.bgColor || DEFAULT_HEADER_BG;
    this.headerTextColor = headerStyle?.textColor || DEFAULT_HEADER_TEXT;
    this.headerFontFamily =
      headerStyle?.fontFamily || DEFAULT_HEADER_FONT_FAMILY;
    this.headerFontSizeNumber = headerStyle?.fontSize
      ? parseInt(String(headerStyle.fontSize).replace("px", ""), 10) ||
        DEFAULT_HEADER_FONT_SIZE
      : DEFAULT_HEADER_FONT_SIZE;
    this.headerFontWeight = headerStyle?.fontWeight || this.headerFontWeight;
    this.headerFontStyle = headerStyle?.fontStyle || this.headerFontStyle;
    this.headerTextAlign = headerStyle?.textAlign || this.headerTextAlign;

    if (headerStyle?.textDecoration) {
      this.headerUnderline = headerStyle.textDecoration.includes("underline");
      this.headerStrike = headerStyle.textDecoration.includes("line-through");
    }

    this.headerHeading = this.mapFontSizeToHeading(
      this.headerFontSizeNumber,
      "header"
    );
  }

  // Helper method to apply body styles from API response
  private applyBodyStyleFromResponse(bodyStyle: any): void {
    const DEFAULT_BODY_BG = "#ffffff";
    const DEFAULT_BODY_TEXT = "#000000";
    const DEFAULT_BODY_FONT_FAMILY = this.headerFonts[0] || "Arial, sans-serif";
    const DEFAULT_BODY_FONT_SIZE = 14;

    this.bodyBgColor = bodyStyle?.bgColor || DEFAULT_BODY_BG;
    this.bodyTextColor = bodyStyle?.textColor || DEFAULT_BODY_TEXT;
    this.bodyFontFamily = bodyStyle?.fontFamily || DEFAULT_BODY_FONT_FAMILY;
    this.bodyFontSizeNumber = bodyStyle?.fontSize
      ? parseInt(String(bodyStyle.fontSize).replace("px", ""), 10) ||
        DEFAULT_BODY_FONT_SIZE
      : DEFAULT_BODY_FONT_SIZE;
    this.bodyFontWeight = bodyStyle?.fontWeight || this.bodyFontWeight;
    this.bodyFontStyle = bodyStyle?.fontStyle || this.bodyFontStyle;
    this.bodyTextAlign = bodyStyle?.textAlign || this.bodyTextAlign;

    if (bodyStyle?.textDecoration) {
      this.bodyUnderline = bodyStyle.textDecoration.includes("underline");
      this.bodyStrike = bodyStyle.textDecoration.includes("line-through");
    }

    this.bodyHeading = this.mapFontSizeToHeading(
      this.bodyFontSizeNumber,
      "body"
    );
  }

  // Utility: scroll dialog content to bottom
  private scrollToBottom() {
    setTimeout(() => {
      try {
        const el = this.dialogContent && this.dialogContent.nativeElement;
        if (!el) return;
        if (typeof el.scroll === "function") {
          el.scroll({
            top: el.scrollHeight,
            behavior: "smooth",
          });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      } catch {
        // ignore
      }
    }, 80);
  }
}
